'use client';

import { create } from 'zustand';

type TabValue = 'map' | 'chat' | 'farm';
type PanelType = 'field' | 'farm' | 'farmProfile' | undefined;

type EducationalTooltipStep = 'one' | 'two' | null

/**
 * A create-modal a deep link asked for. The modals themselves live in
 * `DeepLinkModalHost`, mounted once on `/orth`, because the `+` menu that
 * normally opens them (`CreateBtn`) keeps its own local state and is mounted
 * more than once (side nav, mobile header, mobile navbar).
 */
export type PendingCreate = 'note' | 'action' | null;

// TODO: refactor handling app container values and views such as add field, add farm, chat, and map

// Persist the expanded-chat screen in sessionStorage so a page refresh keeps
// the chat expanded (restored on mount in OrthController).
export const CHAT_EXPANDED_SESSION_KEY = 'chatExpanded';
const persistExpandChat = (expanded: boolean) => {
  if (typeof window === 'undefined') return;
  if (expanded) sessionStorage.setItem(CHAT_EXPANDED_SESSION_KEY, 'true');
  else sessionStorage.removeItem(CHAT_EXPANDED_SESSION_KEY);
};

type MainAppState = {
  expandChat: boolean;
  setExpandChat: (expand: boolean) => void;
  panelType: PanelType;
  setPanelType: (value: PanelType) => void;
  activeTab: TabValue;
  setActiveTab: (value: TabValue) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (value: boolean) => void;
  toggleMobileMenu: () => void;
  showNavProgressBar: boolean;
  setShowNavProgressBar: (value: boolean) => void;
  toggleNavProgressBar: () => void;
  appLoading: boolean;
  setAppLoading: (value: boolean) => void;
  toggleAppLoading: () => void;
  educationalTooltipStep: EducationalTooltipStep
  setEducationalTooltipStep: (step: EducationalTooltipStep) => void;
  pendingCreate: PendingCreate;
  setPendingCreate: (value: PendingCreate) => void;
};

export const useMainAppStore = create<MainAppState>((set, get) => ({
  expandChat: false,
  setExpandChat: (value: boolean) => {
    persistExpandChat(value);
    set({ expandChat: value });
  },
  activeTab: 'map',
  setActiveTab: (value: TabValue) => set({ activeTab: value }),
  panelType: undefined,
  setPanelType: (value: PanelType) => set({ panelType: value }),
  isMobileMenuOpen: false,
  setIsMobileMenuOpen: (value: boolean) => set({ isMobileMenuOpen: value }),
  toggleMobileMenu: () => set({ isMobileMenuOpen: !get().isMobileMenuOpen }),
  showNavProgressBar: false,
  setShowNavProgressBar: (value: boolean) => set({ showNavProgressBar: value }),
  toggleNavProgressBar: () =>
    set({ showNavProgressBar: !get().showNavProgressBar }),
  appLoading: false,
  setAppLoading: (v) => set({ appLoading: v }),
  toggleAppLoading: () => set({ appLoading: !get().appLoading }),
  educationalTooltipStep: null,
  setEducationalTooltipStep: (step) => set({ educationalTooltipStep: step }),
  pendingCreate: null,
  setPendingCreate: (value) => set({ pendingCreate: value }),
}));

